import { useQuery } from '@tanstack/react-query'
import { Calculator, DoorOpen, Wallet } from 'lucide-react'
import { useState } from 'react'

import { tellerSessionListQueryOptions, tillListQueryOptions } from '@/api/teller-api'
import type { TellerSession } from '@/api/teller-types'
import { useAuth } from '@/auth/use-auth'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CashCountDialog } from '@/features/teller/cash-count-dialog'
import {
  DepositRail,
  RepaymentRail,
  SecurityDepositRail,
  WithdrawalRail,
} from '@/features/teller/counter-rails'
import { TELLER_PATHS, useTellerNavigate, useTellerRawSearch } from '@/features/teller/navigation'
import { useTellerPermissions, TELLER_PERMISSIONS } from '@/features/teller/permissions'
import { PostingDayNotice } from '@/features/teller/posting-day-notice'
import { CloseSessionDialog, OpenSessionDialog } from '@/features/teller/session-actions'
import { SessionFiguresPanel } from '@/features/teller/teller-panels'
import { TellerLink } from '@/features/teller/teller-link'
import { usePostingDay } from '@/features/teller/use-posting-day'
import { formatTime } from '@/lib/datetime'

import { tellerCounterSearchSchema, type CounterRailId } from './search'

/**
 * The teller workstation.
 *
 * This is the screen somebody stands at all day with a drawer, a receipt printer, and a queue. It
 * refuses below `lg` rather than shrinking, which docs/mobile-policy.md argues for on physical
 * rather than visual grounds: someone posting a deposit from a phone is not standing where the money
 * is, and keying an amount is the single thing touch is worst at.
 *
 * Everything on it hangs off two facts, read before anything else and shown even when they are
 * inconvenient: the branch's business day, and whether this user has a drawer open. Both can be
 * absent, and the absences are ordinary — a day that has not been opened at seven in the morning, a
 * teller who has not yet taken a till.
 */
export function TellerCounterPage() {
  const { user } = useAuth()
  const permissions = useTellerPermissions()
  const navigate = useTellerNavigate()
  const rawSearch = useTellerRawSearch()
  const day = usePostingDay()

  const [opening, setOpening] = useState(false)
  const [counting, setCounting] = useState(false)
  const [closing, setClosing] = useState(false)

  const search = tellerCounterSearchSchema.parse(rawSearch)
  const rail: CounterRailId = search.rail ?? 'deposit'

  /**
   * A teller's own open shift.
   *
   * There is no "my session" endpoint, so it is found by listing the branch's open sessions and
   * matching on the teller — which is honest, because the list carries `tellerUserId` for exactly
   * this. It needs `teller.till.view`; a role that posts cash without holding it cannot be shown its
   * own drawer, and the panel says so rather than reading as "no session".
   */
  const openSessions = useQuery({
    ...tellerSessionListQueryOptions({ status: 'Open', page: 1, pageSize: 100 }),
    enabled: permissions.canViewTills,
  })

  const tills = useQuery({
    ...tillListQueryOptions({ tillType: 'Counter', status: 'Active', page: 1, pageSize: 100 }),
    enabled: permissions.canViewTills && opening,
  })

  const mySession: TellerSession | null =
    openSessions.data?.items.find((session) => session.tellerUserId === user?.id) ?? null

  const selectRail = (next: CounterRailId) => {
    navigate(TELLER_PATHS.counter, { search: { rail: next }, replace: true })
  }

  const railTabs: TabDefinition<CounterRailId>[] = [
    { id: 'deposit', label: 'Deposit' },
    { id: 'security', label: 'Security deposit' },
    { id: 'withdrawal', label: 'Withdrawal' },
    { id: 'repayment', label: 'Loan repayment' },
  ]

  return (
    <RequiresWiderScreen
      screen="Teller operations"
      reason="Cash is counted at a counter with a till, a drawer, and a receipt printer, and a deposit should be posted where the money is."
    >
      <PageHeader
        title="Counter"
        description="One till, one named user, one open session. Every cash movement posts through the ledger as it happens."
        actions={
          <>
            {mySession ? (
              <>
                <Button variant="secondary" onClick={() => setCounting(true)} disabled={!permissions.canRecordCount}>
                  <Calculator />
                  Count the drawer
                </Button>
                <Button variant="primary" onClick={() => setClosing(true)} disabled={!permissions.canSubmitEod}>
                  <Wallet />
                  Declare and close
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setOpening(true)} disabled={!permissions.canOpenSession}>
                <DoorOpen />
                Open a till
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-4 text-xs">
        <TellerLink
          to={TELLER_PATHS.cashPosition}
          className="font-medium text-brand-text hover:text-brand-text-hover"
        >
          Branch cash position
        </TellerLink>
        <TellerLink
          to={TELLER_PATHS.sessions}
          className="font-medium text-brand-text hover:text-brand-text-hover"
        >
          Today’s shifts
        </TellerLink>
        <TellerLink
          to={TELLER_PATHS.verificationOverrides}
          className="font-medium text-brand-text hover:text-brand-text-hover"
        >
          Verification overrides
        </TellerLink>
      </div>

      <PostingDayNotice day={day} />

      {!permissions.canViewTills ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>Your own drawer is not visible to your role</AlertTitle>
            <AlertDescription>
              Reading a till or a session needs{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.tillView}</span>. The rails
              below still work — the drawer that becomes accountable is resolved by the server from
              your own open session — but nothing here can show you what it holds.
            </AlertDescription>
          </div>
        </Alert>
      ) : openSessions.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : openSessions.isError ? (
        <ErrorSurface
          error={openSessions.error}
          onRetry={() => void openSessions.refetch()}
          title="Your open shift could not be read"
        />
      ) : mySession ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <TellerLink
              to={TELLER_PATHS.session(mySession.id)}
              className="text-sm font-medium text-brand-text hover:text-brand-text-hover"
            >
              Till {mySession.tillNumber} · open since {formatTime(mySession.openedAtUtc)}
            </TellerLink>
            <StatusBadge status={mySession.status} />
            <span className="text-xs text-muted">
              Expected in the drawer{' '}
              <MoneyDisplay
                value={mySession.expectedClosingCashMinor}
                currency={mySession.currencyCode}
                className="inline-block text-left"
              />
            </span>
          </div>
          <SessionFiguresPanel session={mySession} />
        </div>
      ) : (
        <Alert tone="info">
          <div className="space-y-1">
            <AlertTitle>You have no till open</AlertTitle>
            <AlertDescription>
              Cash needs a drawer that is accountable for it, so every rail below is refused with{' '}
              <span className="font-mono text-xs">teller.no_open_till</span> until you open one. A
              shift starts with whatever the drawer already holds, which becomes its opening float —
              count it before you trade.
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="space-y-4">
        <TabStrip<CounterRailId>
          tabs={railTabs}
          active={rail}
          onChange={selectRail}
          panelId="counter-rail-panel"
          label="Counter transaction rails"
        />

        <div
          id="counter-rail-panel"
          role="tabpanel"
          aria-labelledby={`counter-rail-panel-tab-${rail}`}
        >
          {rail === 'deposit' ? (
            <DepositRail day={day} branchId={day.branchId} hasOpenSession={mySession !== null} />
          ) : rail === 'security' ? (
            <SecurityDepositRail day={day} branchId={day.branchId} hasOpenSession={mySession !== null} />
          ) : rail === 'withdrawal' ? (
            <WithdrawalRail day={day} branchId={day.branchId} hasOpenSession={mySession !== null} />
          ) : (
            <RepaymentRail day={day} branchId={day.branchId} hasOpenSession={mySession !== null} />
          )}
        </div>
      </div>

      <OpenSessionDialog
        tills={tills.data?.items ?? []}
        isLoadingTills={tills.isPending && tills.isFetching}
        open={opening}
        onOpenChange={setOpening}
      />

      {mySession ? (
        <>
          <CashCountDialog session={mySession} open={counting} onOpenChange={setCounting} />
          <CloseSessionDialog session={mySession} open={closing} onOpenChange={setClosing} />
        </>
      ) : null}
    </RequiresWiderScreen>
  )
}
